/**
 * Card de estatística moderno para Dashboard
 * Design com ícone em círculo colorido, gradiente de fundo e hover effects
 * Baseado no StatCard das campanhas
 */

import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardStatCardProps {
  /** Título do card */
  title: string;
  /** Valor a exibir */
  value: string;
  /** Descrição adicional abaixo do valor */
  description?: string;
  /** Ícone Lucide a exibir */
  icon: LucideIcon;
  /** Variante de cor do card */
  variant?: 'default' | 'primary' | 'blue' | 'green' | 'purple' | 'yellow' | 'destructive';
  /** Estado de carregamento */
  isLoading?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

// Mapeamento de variantes para estilos
const variantStyles = {
  default: {
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    textColor: 'text-foreground',
    gradient: 'from-background to-muted/30',
  },
  primary: {
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    textColor: 'text-primary',
    gradient: 'from-primary/5 to-primary/10',
  },
  blue: {
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-500',
    textColor: 'text-blue-600',
    gradient: 'from-blue-500/5 to-blue-500/10',
  },
  green: {
    iconBg: 'bg-green-500/10',
    iconColor: 'text-green-500',
    textColor: 'text-green-600',
    gradient: 'from-green-500/5 to-green-500/10',
  },
  purple: {
    iconBg: 'bg-purple-500/10',
    iconColor: 'text-purple-500',
    textColor: 'text-purple-600',
    gradient: 'from-purple-500/5 to-purple-500/10',
  },
  yellow: {
    iconBg: 'bg-yellow-500/10',
    iconColor: 'text-yellow-500',
    textColor: 'text-yellow-600',
    gradient: 'from-yellow-500/5 to-yellow-500/10',
  },
  destructive: {
    iconBg: 'bg-destructive/10',
    iconColor: 'text-destructive',
    textColor: 'text-destructive',
    gradient: 'from-destructive/5 to-destructive/10',
  },
};

export function DashboardStatCard({ 
  title, 
  value, 
  description, 
  icon: Icon, 
  variant = 'default', 
  isLoading,
  className 
}: DashboardStatCardProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        // Padding responsivo: menor em mobile
        'group relative overflow-hidden rounded-xl border bg-gradient-to-br p-2 sm:p-4',
        'transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5',
        styles.gradient,
        className
      )}
    >
      {/* Efeito de brilho no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      {/* Gap responsivo entre ícone e texto */}
      <div className="relative flex items-center gap-2 sm:gap-3">
        {/* Ícone com tamanho responsivo */}
        <div
          className={cn(
            'flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full shrink-0',
            'transition-transform duration-300 group-hover:scale-110',
            styles.iconBg
          )}
        >
          <Icon className={cn('h-4 w-4 sm:h-5 sm:w-5', styles.iconColor)} />
        </div>

        <div className="min-w-0 flex-1">
          {/* Título com fonte menor em mobile */}
          <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">
            {title}
          </p>
          {isLoading ? (
            <>
              <Skeleton className="h-5 sm:h-7 w-12 sm:w-16 mt-1" />
              {description && <Skeleton className="h-2 sm:h-3 w-16 sm:w-24 mt-1" />}
            </>
          ) : (
            <>
              {/* Valor com tamanho responsivo */}
              <p className={cn('text-lg sm:text-2xl font-bold tracking-tight', styles.textColor)}>
                {value}
              </p>
              {description && (
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate mt-0.5">
                  {description}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
